/**
 * useGoals Hook
 * Convenience hook for goal operations with helpful utilities
 */

import { useGoalContext } from '../contexts/GoalContext';
import { Goal, GoalType } from '../models/Goal';
import { GoalAccount } from '../models/GoalAccount';

/**
 * Constants
 */
const MAX_SIDE_GOALS = 10;

export const useGoals = () => {
  const context = useGoalContext();

  /**
   * Check if current account has a main goal
   */
  const hasMainGoal = (): boolean => {
    return context.mainGoal !== null;
  };

  /**
   * Check if can add more side goals (max 10)
   */
  const canAddSideGoal = (): boolean => {
    return context.sideGoals.length < MAX_SIDE_GOALS;
  };

  /**
   * Get remaining side goal slots
   */
  const remainingSideGoalSlots = (): number => {
    return Math.max(0, MAX_SIDE_GOALS - context.sideGoals.length);
  };

  /**
   * Find goal by ID in current account
   */
  const findGoalById = (goalId: string): Goal | undefined => {
    return context.goals.find(g => g.id === goalId);
  };

  /**
   * Find account by ID
   */
  const findAccountById = (accountId: string): GoalAccount | undefined => {
    return context.accounts.find(a => a.id === accountId);
  };

  /**
   * Check if goal exists
   */
  const goalExists = (goalId: string): boolean => {
    return findGoalById(goalId) !== undefined;
  };

  /**
   * Get total goal count for current account
   */
  const totalGoalCount = (): number => {
    return context.goals.length;
  };

  /**
   * Check if current account is valid
   */
  const hasCurrentAccount = (): boolean => {
    return context.currentAccount !== null;
  };

  /**
   * Get current account name
   */
  const currentAccountName = (): string => {
    return context.currentAccount?.name || 'No Account';
  };

  /**
   * Check if account switching is available (more than 1 account)
   */
  const canSwitchAccount = (): boolean => {
    return context.accounts.length > 1;
  };

  /**
   * Create a new goal with validation
   */
  const createGoalSafe = async (name: string, type: GoalType, accountId?: string): Promise<Goal> => {
    // Validate name
    const trimmedName = name.trim();
    if (trimmedName.length === 0) {
      throw new Error('Goal name cannot be empty');
    }

    // Determine which account to validate against
    const targetAccountId = accountId || context.currentAccountId;

    // Validate type-specific constraints for the target account
    if (type === 'main') {
      const targetAccountGoals = context.goals.filter(g => g.accountId === targetAccountId);
      const hasMainGoalInTargetAccount = targetAccountGoals.some(g => g.type === 'main');
      if (hasMainGoalInTargetAccount) {
        throw new Error('Account already has a main goal');
      }
    }

    if (type === 'side') {
      const targetAccountGoals = context.goals.filter(g => g.accountId === targetAccountId);
      const sideGoalsCount = targetAccountGoals.filter(g => g.type === 'side').length;
      if (sideGoalsCount >= MAX_SIDE_GOALS) {
        throw new Error('Maximum 10 side goals per account');
      }
    }

    return await context.createNewGoal(trimmedName, type, accountId);
  };

  /**
   * Update goal with validation
   */
  const updateGoalSafe = async (
    goalId: string,
    updates: Partial<Omit<Goal, 'id' | 'accountId' | 'createdAt'>>
  ): Promise<void> => {
    // Validate goal exists
    if (!goalExists(goalId)) {
      throw new Error(`Goal not found: ${goalId}`);
    }

    // Validate name if provided
    if (updates.name !== undefined) {
      const trimmedName = updates.name.trim();
      if (trimmedName.length === 0) {
        throw new Error('Goal name cannot be empty');
      }
      updates.name = trimmedName;
    }

    await context.updateExistingGoal(goalId, updates);
  };

  /**
   * Delete goal with confirmation
   */
  const deleteGoalSafe = async (goalId: string): Promise<void> => {
    // Validate goal exists
    if (!goalExists(goalId)) {
      throw new Error(`Goal not found: ${goalId}`);
    }

    await context.deleteExistingGoal(goalId);
  };

  /**
   * Create account with validation
   */
  const createAccountSafe = async (name: string, themeColor?: string): Promise<GoalAccount> => {
    const trimmedName = name.trim();
    if (trimmedName.length === 0) {
      throw new Error('Account name cannot be empty');
    }

    return await context.createNewAccount(trimmedName, themeColor);
  };

  /**
   * Update account with validation
   */
  const updateAccountSafe = async (
    accountId: string,
    updates: Partial<Omit<GoalAccount, 'id' | 'createdAt'>>
  ): Promise<void> => {
    // Validate account exists
    if (!findAccountById(accountId)) {
      throw new Error(`Account not found: ${accountId}`);
    }

    // Validate name if provided
    if (updates.name !== undefined) {
      const trimmedName = updates.name.trim();
      if (trimmedName.length === 0) {
        throw new Error('Account name cannot be empty');
      }
      updates.name = trimmedName;
    }

    await context.updateExistingAccount(accountId, updates);
  };

  /**
   * Delete account with validation
   */
  const deleteAccountSafe = async (accountId: string): Promise<void> => {
    // Validate account exists
    if (!findAccountById(accountId)) {
      throw new Error(`Account not found: ${accountId}`);
    }

    // Prevent deleting last account
    const activeAccounts = context.accounts.filter(a => !a.isDeleted);
    if (activeAccounts.length <= 1) {
      throw new Error('Cannot delete the last account');
    }

    await context.deleteExistingAccount(accountId);
  };

  return {
    // Context state
    ...context,

    // Validation helpers
    hasMainGoal,
    canAddSideGoal,
    remainingSideGoalSlots,
    hasCurrentAccount,
    canSwitchAccount,

    // Lookup helpers
    findGoalById,
    findAccountById,
    goalExists,
    totalGoalCount,
    currentAccountName,

    // Safe operations with validation
    createGoalSafe,
    updateGoalSafe,
    deleteGoalSafe,
    createAccountSafe,
    updateAccountSafe,
    deleteAccountSafe,
  };
};
